import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { VolumeOff, VolumeUp, Play } from 'react-iconly';

const ScrollVideoPlayer = ({ src, overlayText, children, containerHeight = "200vh" }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const scale = useTransform(scrollYProgress, [0.1, 0.35, 0.65, 0.9], [0.8, 1, 1, 0.8]);
  const borderRadius = useTransform(scrollYProgress, [0.1, 0.35, 0.65, 0.9], ["24px", "0px", "0px", "24px"]);
  
  // Only apply fade effect if overlayText is provided
  const textOpacity = useTransform(scrollYProgress, [0.4, 0.6, 0.8], [0, 1, 0]);
  const fadeOpacity = useTransform(scrollYProgress, [0.3, 0.5], [0, 1]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setProgress((video.currentTime / video.duration) * 100);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, []);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const togglePlay = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch(() => {
          setIsPlaying(false);
        });
      }
    }
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (video) {
      const seekTime = (parseFloat(e.target.value) / 100) * video.duration;
      video.currentTime = seekTime;
      setProgress(e.target.value);
    }
  };

  return (
    <section ref={containerRef} style={{ height: containerHeight, position: 'relative', width: '100%' }}>
      <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent' }}>
        <motion.div 
          style={{ 
            scale,
            borderRadius,
            width: '100%',
            height: '100vh',
            overflow: 'hidden',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1
          }}
        >
          <video 
            ref={videoRef}
            src={src}
            autoPlay 
            loop 
            muted={isMuted}
            playsInline 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />

          {children}

          {/* Custom Video Controls */}
          <div style={{
            position: 'absolute',
            bottom: '32px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'calc(100% - 64px)',
            maxWidth: '400px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 24px',
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '100px',
            zIndex: 10,
            color: 'white'
          }}>
            <button onClick={togglePlay} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}>
              {isPlaying ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 5V19M16 5V19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : <Play size={24} set="bold" />}
            </button>
            
            <input 
              type="range" 
              min="0" 
              max="100" 
              step="0.1"
              value={progress || 0}
              onChange={handleSeek}
              className="custom-video-scrubber"
              style={{
                flex: 1,
                cursor: 'pointer',
                accentColor: 'white',
                height: '4px',
                borderRadius: '2px',
                WebkitAppearance: 'none',
                background: `linear-gradient(to right, white ${progress}%, rgba(255,255,255,0.2) ${progress}%)`,
                outline: 'none'
              }}
            />

            <button onClick={toggleMute} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}>
              {isMuted ? <VolumeOff size={24} set="bold" /> : <VolumeUp size={24} set="bold" />}
            </button>
          </div>
        </motion.div>

        {overlayText && (
          <>
            <motion.div style={{ position: 'absolute', zIndex: 2, opacity: textOpacity }}>
              <h2 className="v2-display-l" style={{ color: 'var(--v2-color-charcoal)', textAlign: 'center' }}>{overlayText}</h2>
            </motion.div>
            
            <motion.div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'var(--v2-color-offwhite)',
              opacity: fadeOpacity,
              zIndex: 1.5,
              pointerEvents: 'none'
            }} />
          </>
        )}
      </div>
    </section>
  );
};

export default ScrollVideoPlayer;
